<template lang="pug">
.FcwMap(:style="style")
  Transition(name="fade")
    .FcwMap__loader(v-if="loading")
      .FcwMap__loader__content
        FLoader(
          color="primary"
          width="64px"
        )
        span {{ t('mapbox__loading') }}
  .FcwMap__container(
    ref="mapRef"
    :class="classes"
  )
    slot
</template>

<style lang="stylus">
.FcwMap
  .fade-enter-active,
  .fade-leave-active
    transition opacity 0.2s ease-in-out

  .fade-leave-to,
  .fade-enter-from
    opacity 0

.FcwMap
  position relative
  height var(--FcwMap--height)
  width var(--FcwMap--width)

.FcwMap__loader
  position absolute
  top 0
  right 0
  bottom 0
  left 0
  z-index 1
  display flex
  justify-content center
  align-items center
  height var(--FcwMap--height)
  width var(--FcwMap--width)

  .FLoader
    margin auto auto rem(16)

  &::before
    content ''
    position absolute
    width 100%
    height 100%
    background var(--color--primary--light-2)

.FcwMap__loader__content
  z-index 1

.FcwMap__container
  height 100%

  &--rounded
    border-radius rem(16)

  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right,
  .mapboxgl-ctrl-top-left,
  .mapboxgl-ctrl-top-right
    z-index 0

  .mapboxgl-ctrl-attrib-inner
    a
      font-size rem(12)

  .mapboxgl-marker
    cursor pointer

  .mapboxgl-popup-content
    display flex
    flex-direction column
    align-items center
    justify-content center
</style>

<script setup lang="ts">
import {
  Map,
  ScaleControl,
  NavigationControl,
  GeolocateControl,
  LngLatBounds,
} from 'mapbox-gl';
import { genSize } from '@fifteen/design-system-vue';

import { CwAppErrorCode } from '@/models/enums';
import { sleep } from '@/lib/utils';

import 'mapbox-gl/dist/mapbox-gl.css';

import type { FitBoundsOptions, LngLatLike } from 'mapbox-gl';
import type { Ref } from 'vue';

export type Control = 'scale' | 'navigation' | 'geolocate';

export interface FcwMapProps {
  /**
   * Controls to enable on map
   */
  controls?: Control[];
  /**
   * Controls position on the map
   */
  controlsPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /**
   * Center of the map
   */
  center?: LngLatLike;
  /**
   * Minimum zoom of the map
   */
  minZoom?: number;
  /**
   * Maximum zoom of the map
   */
  maxZoom?: number;
  /**
   * Initial zoom of the map
   */
  initialZoom?: number;
  /**
   * Enable or not the scroll zoom
   */
  scrollZoom?: boolean;
  /**
   * Enable or not the map rotation
   */
  enableRotation?: boolean;
  /**
   * If true, user interactions with the map on touch devices are required to be made with two fingers
   */
  enableCooperativeGestures?: boolean;
  /**
   * Shape of the corners
   */
  corners?: 'sharp' | 'rounded';
  /**
   * Loading state of the map
   */
  loading?: boolean;
  /**
   * Height of the map
   */
  height?: string;
  /**
   * Width of the map
   */
  width?: string;
}

const props = withDefaults(defineProps<FcwMapProps>(), {
  controls: () => [],
  controlsPosition: 'top-right',
  center: () => [0, 0],
  scrollZoom: false,
  enableRotation: false,
  enableCooperativeGestures: false,
  corners: 'sharp',
  loading: false,
  height: 'auto',
  width: 'auto',
  minZoom: 0,
  maxZoom: 20,
  initialZoom: 13,
});

const { t } = useClientI18n();
const config = useRuntimeConfig();

const mapRef = ref<HTMLElement>();
const map = ref<Map>();
const isMapLoaded = ref(false);
const isMapCentered = ref(false);

export interface FcwMapProvide {
  map: Ref<Map | undefined>;
  isMapLoaded: Ref<boolean>;
}

provide<FcwMapProvide>('map', { map, isMapLoaded });

const emit = defineEmits<{
  (name: 'load', map: Ref<Map | undefined>): void;
  (name: 'geolocate', map: Ref<Map | undefined>): void;
}>();

defineExpose({
  fitBounds,
  flyTo,
  easeTo,
  project,
});

onMounted(() => {
  if (!mapRef.value) return;

  map.value = new Map({
    fadeDuration: 0,
    container: mapRef.value,
    accessToken: config.public.mapboxAccessToken,
    style: config.public.mapboxStyle,
    center: props.center,
    scrollZoom: props.scrollZoom,
    dragRotate: props.enableRotation,
    touchZoomRotate: props.enableRotation,
    cooperativeGestures: props.enableCooperativeGestures,
    minZoom: props.minZoom ?? 0,
    maxZoom: props.maxZoom ?? 20,
    zoom: props.initialZoom ?? 13,
    pitch: 0,
    locale: {
      'ScrollZoomBlocker.CtrlMessage': t('mapbox__use_ctrl_scroll_to_zoom'),
      'ScrollZoomBlocker.CmdMessage': t('mapbox__use_cmd_scroll_to_zoom'),
      'TouchPanBlocker.Message': t('mapbox__use_two_fingers_to_move_the_map'),
    },
  });

  map.value.on('load', () => {
    addControls();
    isMapLoaded.value = true;
    emit('load', map);
  });

  // Listen 'moveend' event that is emitted internally after a map center set
  // It sets a flag that is used to be sure that fit bounds works correctly
  map.value.on('moveend', () => {
    isMapCentered.value = true;
  });
});

useResizeObserver(mapRef, () => {
  map.value?.resize();
});

async function flyTo(
  center: LngLatLike,
  options?: { duration?: number; zoom?: number; offset?: [number, number] }
): Promise<void> {
  if (!map.value) return;

  await until(isMapLoaded).toBe(true);

  map.value.flyTo({ center, ...options });
}

async function easeTo(
  center: LngLatLike,
  options?: mapboxgl.EaseToOptions
): Promise<void> {
  if (!map.value) return;

  await until(isMapLoaded).toBe(true);

  map.value.easeTo({ center, ...options });
}

async function project(
  lngLat: LngLatLike
): Promise<{ x: number; y: number } | undefined> {
  if (!map.value) return;

  await until(isMapLoaded).toBe(true);

  return map.value.project(lngLat);
}

async function fitBounds(
  coordinates: Coordinates[],
  options?: FitBoundsOptions
): Promise<void> {
  if (!coordinates.length) return;

  await Promise.all([
    until(isMapLoaded).toBe(true),
    Promise.race([until(isMapCentered).toBe(true), sleep(20)]),
  ]);

  map.value?.fitBounds(
    coordinates.reduce(
      (bounds, coord) => bounds.extend(coord),
      new LngLatBounds(coordinates[0], coordinates[0])
    ),
    options
  );
}

function addControls(): void {
  if (!map.value) return;

  if (props.controls?.includes('scale')) {
    map.value.addControl(new ScaleControl(), props.controlsPosition);
  }

  if (props.controls?.includes('navigation')) {
    map.value.addControl(new NavigationControl(), props.controlsPosition);
  }

  if (props.controls?.includes('geolocate')) {
    const geolocateControl = new GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      showAccuracyCircle: false,
      trackUserLocation: true,
    });

    geolocateControl.on('error', () => {
      useErrorAlert(CwAppErrorCode.UserGeolocationFailed);
    });

    map.value.addControl(geolocateControl, props.controlsPosition);
  }
}

watch(
  () => props.scrollZoom,
  () => {
    if (!map.value) return;
    props.scrollZoom
      ? map.value.scrollZoom.enable()
      : map.value.scrollZoom.disable();
  }
);

const classes = computed<VueClasses>(() => ({
  'FcwMap--rounded': props.corners === 'rounded',
}));

const style = computed<Style>(() => ({
  '--FcwMap--height': genSize(props.height),
  '--FcwMap--width': genSize(props.width),
}));
</script>
